import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AuditionRoom from "@/pages/AuditionRoom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { initParticles } from "./particles";

// Pages
import LoginPage from "@/pages/LoginPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import CallbackPage from "@/pages/CallbackPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminOrgList from "@/pages/admin/AdminOrgList";
import AdminOrgDetail from "@/pages/admin/AdminOrgDetail";
import SignalRoom from "@/pages/chro/SignalRoom";
import BlueprintRoom from "@/pages/chro/BlueprintRoom";
import Stage from "@/pages/chro/Stage";
import Showcase from "@/pages/chro/Showcase";
import SharedSignalRoom from "@/pages/SharedSignalRoom";
import InvitePage from "@/pages/InvitePage";
import Analysis from "@/pages/chro/Analysis";
import AnalysisDiscipline from "@/pages/chro/AnalysisDiscipline";
import AnalysisResults from "@/pages/chro/AnalysisResults";
import BlueprintCockpit from "@/pages/chro/BlueprintCockpit";
import ShowcaseAI from "@/pages/chro/ShowcaseAI";
import OrgIntelligence from "@/pages/chro/OrgIntelligence";
import Deconstruct from "@/pages/chro/Deconstruct";
import Analyze from "@/pages/chro/Analyze";
import Sort4B from "@/pages/chro/Sort4B";
import Recompose from "@/pages/chro/Recompose";
import DemoEntry from "@/pages/DemoEntry";
import CandidatePortal from "@/pages/CandidatePortal";
import AdminDemos from "@/pages/admin/AdminDemos";
import AdminActivity from "@/pages/admin/AdminActivity";
import Sherpa from "@/components/Sherpa";

// Suppress ResizeObserver benign loop warnings
// (Chrome fires "ResizeObserver loop completed with undelivered notifications"
// when a canvas resize triggers a same-frame redraw. Harmless in our case
// since all observers schedule via rAF, but CRA's dev overlay still catches
// the window.onerror.)
if (typeof window !== 'undefined') {
  const _resizeObserverErr = window.onerror;
  window.onerror = (msg, ...args) => {
    if (typeof msg === 'string' && msg.includes('ResizeObserver')) return true;
    return _resizeObserverErr?.(msg, ...args);
  };
}

// Protected route wrapper
const ProtectedRoute = ({ children, allowedRoles = [], adminRoute = false }) => {
  const { isAuthenticated, role, loading } = useAuth();

  // Always check localStorage directly as source of truth
  const localToken = localStorage.getItem('token');
  const localRole = localStorage.getItem('role');
  const isDemo = localStorage.getItem('is_demo') === 'true';

  // If we have a token in localStorage, trust it immediately — don't wait for auth state
  if (localToken) {
    if (allowedRoles.length > 0 && !allowedRoles.includes(localRole)) {
      return <Navigate to="/" replace />;
    }
    return children;
  }

  // No local token — check auth state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={adminRoute ? "/admin/login" : "/login"} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Home redirect based on role
const HomeRedirect = () => {
  const { isAuthenticated, isAdmin, isChro } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (isChro) {
    return <Navigate to="/signal-room" replace />;
  }

  return <Navigate to="/login" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/callback" element={<CallbackPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/shared/:linkId" element={<SharedSignalRoom />} />
      <Route path="/demo/:slug" element={<DemoEntry />} />
      <Route path="/audition/:token" element={<AuditionRoom />} />
      <Route path="/candidate/:searchId/:candidateId" element={<CandidatePortal />} />

      {/* Analysis flow (public - no auth required for demo) */}
      <Route path="/analysis" element={<Analysis />} />
      <Route path="/analysis/discipline" element={<AnalysisDiscipline />} />
      <Route path="/analysis/results" element={<AnalysisResults />} />
      <Route path="/blueprint/cockpit" element={<BlueprintCockpit />} />
      <Route path="/showcase/ai-tools" element={<ShowcaseAI />} />

      {/* Home redirect */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "operator"]} adminRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orgs"
        element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "operator"]} adminRoute>
            <AdminOrgList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orgs/:orgId"
        element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "operator"]} adminRoute>
            <AdminOrgDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/demos"
        element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "operator"]} adminRoute>
            <AdminDemos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/activity"
        element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "operator"]} adminRoute>
            <AdminActivity />
          </ProtectedRoute>
        }
      />

      {/* CHRO routes */}
      <Route
        path="/signal-room"
        element={
          <ProtectedRoute allowedRoles={["chro", "demo"]}>
            <SignalRoom />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intelligence"
        element={
          <ProtectedRoute allowedRoles={["chro", "demo"]}>
            <OrgIntelligence />
          </ProtectedRoute>
        }
      />
      <Route
        path="/blueprint"
        element={
          <ProtectedRoute allowedRoles={["chro", "demo"]}>
            <BlueprintRoom />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stage"
        element={
          <ProtectedRoute allowedRoles={["chro", "demo"]}>
            <Stage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deconstruct"
        element={
          <ProtectedRoute allowedRoles={["chro", "demo"]}>
            <Deconstruct />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analyze"
        element={
          <ProtectedRoute allowedRoles={["chro", "demo"]}>
            <Analyze />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sort"
        element={
          <ProtectedRoute allowedRoles={["chro", "demo"]}>
            <Sort4B />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recompose"
        element={
          <ProtectedRoute allowedRoles={["chro", "demo"]}>
            <Recompose />
          </ProtectedRoute>
        }
      />
      <Route
        path="/showcase/:blueprintId"
        element={
          <ProtectedRoute allowedRoles={["chro", "demo"]}>
            <Showcase />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const cv = document.getElementById('px');
    if (!cv) return;
    const api = initParticles(cv, { mode: 'ambient' });
    window.__particles = api;
    return () => api.destroy();
  }, []);

  return (
    <>
      <canvas id="px" style={{position:'fixed',inset:0,pointerEvents:'none',zIndex:1,width:'100vw',height:'100vh'}} />
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-background">
            <AppRoutes />
            <Toaster position="top-right" richColors />
            <Sherpa />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </>
  );
}

export default App;
